import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { UserOutlined, MailOutlined, LockOutlined, GithubOutlined } from '@ant-design/icons';
import { Button, Form, Input, Typography, Divider } from 'antd';
import { RuleObject } from 'antd/es/form';
import { getGitHubOAuthAuthorizationURL } from '../../utils/accounts';
import { LOGIN_ROUTE } from '../../config/constants';
import styles from './SignUpForm.module.css';

// sign up form values
export interface SignUpInfo {
  username: string
  email: string
  password: string
  confirmPassword: string
};

export interface SignUpFormProps {
  handleSignUp: (info: SignUpInfo) => Promise<void>
};

// form used on sign up page
const SignUpForm = (props: SignUpFormProps): JSX.Element | null => {
  // password input visibility state
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  // sign up call enabled/disabled state
  const [signUpDisabled, setSignUpDisabled] = useState<boolean>(false);

  // handle sign up
  const handleSignUp = (info: SignUpInfo): void => {
    if (signUpDisabled) {
      return;
    }

    setSignUpDisabled(true);
    props.handleSignUp(info).finally(
      () => setSignUpDisabled(false)
    );
  };

  // redirect to authorization url
  const handleGitHubLogin = () => {
    window.location.href = getGitHubOAuthAuthorizationURL();
  };

  return (
    <Form
      name='basic'
      layout={'vertical'}
      initialValues={{ remember: true }}
      onFinish={handleSignUp}
    >
      <Form.Item
        label={
          <Typography.Text
            className={styles.formLabel}
          >
            Username
          </Typography.Text>
        }
        name='username'
        rules={[
          {
            required: true,
            message: (
              <Typography.Text
                className={styles.formAlert}
              >
                Please enter a username
              </Typography.Text>
            )
          },
          {
            pattern: /^[A-Za-z0-9._]+$/,
            message: (
              <Typography.Text
                className={styles.formAlert}
              >
                Please use only alphabets, digits,<br />periods (.), and underscores (_)
              </Typography.Text>
            )
          }
        ]}
      >
        <Input
          name='username'
          placeholder='Enter username'
          prefix={<UserOutlined />}
          disabled={signUpDisabled}
        />
      </Form.Item>
      <Form.Item
        label={
          <Typography.Text
            className={styles.formLabel}
          >
            Email
          </Typography.Text>
        }
        name='email'
        rules={[
          {
            type: 'email',
            message: (
              <Typography.Text
                className={styles.formAlert}
              >
                Please enter a valid email
              </Typography.Text>
            )
          },
          {
            required: true,
            message: (
              <Typography.Text
                className={styles.formAlert}
              >
                Please enter an email
              </Typography.Text>
            )
          }
        ]}
      >
        <Input
          name='email'
          placeholder='Enter email'
          prefix={<MailOutlined />}
          disabled={signUpDisabled}
        />
      </Form.Item>
      <Form.Item
        label={
          <Typography.Text
            className={styles.formLabel}
          >
            Password
          </Typography.Text>
        }
        name='password'
        rules={[
          {
            required: true,
            message: (
              <Typography.Text
                className={styles.formAlert}
              >
                Please enter a password
              </Typography.Text>
            )
          }
        ]}
      >
        <Input.Password
          name='password'
          placeholder='Enter password'
          prefix={<LockOutlined />}
          visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
          disabled={signUpDisabled}
        />
      </Form.Item>
      <Form.Item
        label={
          <Typography.Text
            className={styles.formLabel}
          >
            Confirm Password
          </Typography.Text>
        }
        name='confirmPassword'
        dependencies={['password']}
        rules={[
          {
            required: true,
            message: (
              <Typography.Text
                className={styles.formAlert}
              >
                Please confirm your password
              </Typography.Text>
            )
          },
          ({ getFieldValue }) => ({
            message: (
              <Typography.Text
                className={styles.formAlert}
              >
                Passwords do not match
              </Typography.Text>
            ),
            validator: (rule: RuleObject, value: any) => {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }

              return Promise.reject(
                new Error('Passwords do not match')
              );
            }
          })
        ]}
      >
        <Input.Password
          name='confirmPassword'
          placeholder='Re-enter password'
          prefix={<LockOutlined />}
          visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
          disabled={signUpDisabled}
        />
      </Form.Item>
      <Form.Item className={styles.submitButtonContainer}>
        <Button
          type='primary'
          htmlType='submit'
          loading={signUpDisabled}
          className='mainButton'
        >
          Sign Up
        </Button>
      </Form.Item>
      <Divider plain>
        <Typography.Text
          className={styles.formLabel}
        >
          Or sign up using
        </Typography.Text>
      </Divider>
      <Form.Item className={styles.gitHubOAuthButtonItem}>
        <Button
          type='primary'
          onClick={handleGitHubLogin}
          className={styles.gitHubOAuthButton}
        >
          <GithubOutlined />
          GitHub
        </Button>
      </Form.Item>
      <Form.Item className={styles.loginTextItem}>
        <Typography.Text
          className={styles.formLabelSmall}
        >
          Already have an account?&nbsp;
          <Link to={LOGIN_ROUTE}>
            Login!
          </Link>
        </Typography.Text>
      </Form.Item>
    </Form>
  );
};

export default SignUpForm;
